import type { ReactNode } from 'react';

import { ERROR_MESSAGES } from '@src/constants/ErrorMessages';

class CustomError extends Error {
  code: string | number;

  constructor(message: string, code: string | number) {
    super(message);
    this.code = code;
    this.message = message;
  }
}

const isPermissionErrorMessage = (message: unknown): boolean => {
  const output = String(message || '');
  return (
    /User not authorized/i.test(output) ||
    /Operation not allowed/i.test(output) ||
    /permission-denied/i.test(output) ||
    /permissionDenied/i.test(output)
  );
};

const BuildingError = (message: string): ReactNode => {
  const output = message;

  if (process.env.NODE_ENV === 'development' && output) {
    // eslint-disable-next-line no-console
    console.log('BuildingError:', output);
  }

  if (output.includes('requires an index')) {
    return ERROR_MESSAGES.FIREBASE_INDEX;
  }

  if (output.includes('Cannot read properties of undefined')) {
    return ERROR_MESSAGES.PROP_NOT_FOUND;
  }

  if (isPermissionErrorMessage(output)) {
    return ERROR_MESSAGES.PERMISSION_DENIED;
  }

  return ERROR_MESSAGES.DEFAULT;
};

export type ErrorResultPresentation = {
  status: '403' | '500';
  title: ReactNode;
  subTitle: ReactNode | null;
};

/**
 * Presentation props for Ant Design Result when BoxContainer shows an error.
 */
const buildErrorResult = (errorName: string): ErrorResultPresentation => {
  const isPermission = isPermissionErrorMessage(errorName);

  return {
    status: isPermission ? '403' : '500',
    title: isPermission ? ERROR_MESSAGES.PERMISSION_DENIED : ERROR_MESSAGES.DEFAULT,
    // Avoid duplicating PERMISSION_DENIED as both title and subtitle.
    subTitle: isPermission ? null : BuildingError(errorName),
  };
};

export { CustomError, BuildingError, buildErrorResult, isPermissionErrorMessage };
